import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Transaction {
  id: number;
  date: string;
  description: string;
  amount: number;
  category: string;
  account: string;
  status: 'cleared' | 'pending' | 'reconciled';
  reference?: string;
}

// Placeholder data - will be replaced with real API
const transactions: Transaction[] = [
  { id: 1, date: '2026-04-02', description: 'Office Supplies - Staples', amount: -45.67, category: 'Office Expense', account: 'Operating Bank Account', status: 'cleared' },
  { id: 2, date: '2026-04-01', description: 'Client Payment - Invoice #1042', amount: 2500.00, category: 'Revenue', account: 'Operating Bank Account', status: 'reconciled' },
  { id: 3, date: '2026-04-01', description: 'Software Subscription - Adobe', amount: -89.99, category: 'Software', account: 'Operating Bank Account', status: 'cleared' },
  { id: 4, date: '2026-03-31', description: 'Insurance Payment', amount: -450.00, category: 'Insurance', account: 'Operating Bank Account', status: 'reconciled' },
  { id: 5, date: '2026-03-30', description: 'Client Payment - Invoice #1041', amount: 1800.00, category: 'Revenue', account: 'Operating Bank Account', status: 'reconciled' },
  { id: 6, date: '2026-03-29', description: 'Internet Service', amount: -120.00, category: 'Utilities', account: 'Operating Bank Account', status: 'pending' },
];

export function Transactions() {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA');
  };

  const getStatusVariant = (status: Transaction['status']): 'secondary' | 'outline' | 'default' => {
    switch (status) {
      case 'reconciled': return 'secondary';
      case 'cleared': return 'secondary';
      case 'pending': return 'outline';
      default: return 'default';
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Transactions</h1>
          <p className="text-sm text-muted-foreground">Bank transactions imported from connected accounts</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm">
            <i className="bi bi-funnel"></i> Filter
          </Button>
          <Button variant="outline" size="sm">
            <i className="bi bi-download"></i> Export
          </Button>
          <Button variant="default" size="sm">
            <i className="bi bi-plus"></i> Add Transaction
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Date Range</label>
              <div className="flex items-center gap-2">
                <input type="date" className="px-3 py-2 text-sm border border-input rounded-md bg-background" defaultValue="2026-03-01" />
                <span className="text-muted-foreground">to</span>
                <input type="date" className="px-3 py-2 text-sm border border-input rounded-md bg-background" defaultValue="2026-04-02" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Account</label>
              <select className="px-3 py-2 text-sm border border-input rounded-md bg-background">
                <option>All Accounts</option>
                <option>Operating Bank Account</option>
                <option>Savings Account</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Status</label>
              <select className="px-3 py-2 text-sm border border-input rounded-md bg-background">
                <option>All Statuses</option>
                <option>Reconciled</option>
                <option>Cleared</option>
                <option>Pending</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle>Transactions ({transactions.length})</CardTitle>
            <div className="flex gap-4 text-sm">
              <span className="flex items-center gap-1">
                <i className="bi bi-arrow-up text-green-600"></i>
                {formatCurrency(transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0))}
              </span>
              <span className="flex items-center gap-1">
                <i className="bi bi-arrow-down text-red-600"></i>
                {formatCurrency(transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0))}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Date</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Description</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Category</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Account</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Amount</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4 font-mono text-sm">{formatDate(tx.date)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        {tx.description}
                        {tx.reference && (
                          <span className="text-xs text-muted-foreground">Ref: {tx.reference}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">{tx.category}</td>
                    <td className="py-3 px-4">{tx.account}</td>
                    <td className="py-3 px-4">
                      <Badge variant={getStatusVariant(tx.status)}>
                        {tx.status}
                      </Badge>
                    </td>
                    <td className={`py-3 px-4 text-right font-mono ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount >= 0 ? '+' : '-'}{formatCurrency(tx.amount)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm">
                          <i className="bi bi-eye"></i>
                        </Button>
                        <Button variant="ghost" size="sm">
                          <i className="bi bi-pencil"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <Button variant="outline" size="sm" disabled>
          <i className="bi bi-chevron-left"></i> Previous
        </Button>
        <span className="text-sm text-muted-foreground">Page 1 of 1</span>
        <Button variant="outline" size="sm" disabled>
          Next <i className="bi bi-chevron-right"></i>
        </Button>
      </div>
    </div>
  );
}